<template>
  <div class="area-select-box">
    <el-select
      :model-value="values[0]"
      :placeholder="provPlaceholder"
      clearable
      :disabled="disabled"
      filterable
      autocomplete="nony"
      @change="onProvChange"
      @keyup.enter="onKeyupEnter"
    >
      <el-option
        v-for="(v, idx) in provinces"
        :key="idx"
        :value="v.value"
        :label="v.label"
      />
    </el-select>
    <el-select
      :model-value="values[1]"
      :disabled="isNull(values[0])||disabled"
      class="asb-city"
      :placeholder="cityPlaceholder"
      clearable
      filterable
      autocomplete="nony"
      @change="onCityChange"
      @keyup.enter="onKeyupEnter"
    >
      <el-option
        v-for="(v, idx) in cities"
        :key="idx"
        :value="v.value"
        :label="v.label"
      />
    </el-select>
    <el-select
      v-if="enableTown"
      class="asb-town"
      :model-value="values[2]"
      :disabled="isNull(values[1])||disabled"
      clearable
      :placeholder="townPlaceholder"
      @change="onTownChange"
    >
      <el-option
        v-for="(v, idx) in towns"
        :key="idx"
        :value="v.value"
        :label="v.label"
      />
    </el-select>
  </div>
</template>

<script setup lang="ts">
import { PropType, ref, watch } from 'vue';
import { getAreasByCityCode, getCitiesByProvCode, getProvinceList, IAreaItem, isNull } from '@core/tools';

defineOptions({ name: 'AreaSelect' });

const emit = defineEmits<{
  'key-enter': [];
  change: [value: string[]];
}>();

const props = defineProps({
  initialCodes: {
    type: Array as PropType<string[]>,
    required: true,
  },
  enableTown: {
    type: Boolean,
    default: true,
  },
  enableEnterKey: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  provPlaceholder: {
    type: String,
    default: '请选择',
  },
  cityPlaceholder: {
    type: String,
    default: '请选择',
  },
  townPlaceholder: {
    type: String,
    default: '请选择',
  },
});

const values = ref<string[]>([]);

const provinces = getProvinceList();
const cities = ref<IAreaItem[]>([]);
const towns = ref<IAreaItem[]>([]);

function onProvChange(provCode: string) {
  values.value[0] = provCode;
  values.value[1] = '';
  values.value[2] = '';
  cities.value = getCitiesByProvCode(provCode);
  emit('change', [values.value[0], undefined, undefined]);
}

function onCityChange(cityCode: string) {
  values.value[1] = cityCode;
  values.value[2] = '';
  towns.value = getAreasByCityCode(values.value[0], cityCode);
  emit('change', [values.value[0], values.value[1], undefined]);
}

function onTownChange(townCode: string) {
  values.value[2] = townCode;
  emit('change', values.value);
}

async function onKeyupEnter() {
  if (!props.enableEnterKey) return;

  emit('key-enter');
}

watch(
  () => props.initialCodes,
  (codes) => {
    if (codes[0] && codes[1]) {
      cities.value = getCitiesByProvCode(codes[0]);
    }

    if (codes[0] && codes[1] && codes[2]) {
      towns.value = getAreasByCityCode(codes[0], codes[1]);
    }

    values.value = codes.map((v) => isNull(v) ? undefined : v);
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.area-select-box {
  display: flex;
  width: 100%;

  .el-select {
    flex: 1 1;
  }
}

:deep(.asb-city),
:deep(.asb-town) {
  margin-left: 9px;
}
</style>
